import BackspaceIcon from '@mui/icons-material/Backspace';
import CallEndIcon from '@mui/icons-material/CallEnd';
import RecordIcon from '@mui/icons-material/FiberManualRecord';
import PauseIcon from '@mui/icons-material/Pause';
import PhoneIcon from '@mui/icons-material/Phone';
import PlayIcon from '@mui/icons-material/PlayArrow';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';

import { formatForDisplay, toE164 } from '../../../utils/phone-format';
import { Dialpad } from '../../atoms/Dialpad';
import { AgentStatusMenu } from '../../molecules/AgentStatusMenu';
import { useConnect } from '../ConnectProvider';

// Primary action panel: agent status menu, phone-number input, dialpad,
// and call/hangup button. The dialpad sends DTMF when a call is live; this
// stage just appends to the input — DTMF send is wired in a follow-up PR.

export const DialPanel: React.FC = () => {
  const {
    agent,
    contact,
    setStatus,
    dial,
    hangup,
    initState,
    recordingState,
    pauseRecording,
    resumeRecording,
  } = useConnect();
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [recordingBusy, setRecordingBusy] = useState(false);
  // Single in-flight flag for dial / hangup. Prevents rapid clicks from
  // firing duplicate SDK calls before the contact event propagates.
  const [actionBusy, setActionBusy] = useState(false);

  const isCallActive = contact !== null && contact.state !== 'ended';
  const isReady = initState.status === 'ready';

  const togglePauseResume = async () => {
    setError(null);
    setRecordingBusy(true);
    try {
      if (recordingState === 'paused') {
        await resumeRecording();
      } else {
        await pauseRecording();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to change recording state');
    } finally {
      setRecordingBusy(false);
    }
  };

  const onDigit = (d: string) => {
    // No-op while a call is live — the dialpad is purely for composing the
    // next number; DTMF send during an in-progress call is not yet wired in,
    // so digits would otherwise leak silently into the next dial draft.
    if (isCallActive) return;
    setError(null);
    setDraft((prev) => prev + d);
  };

  const onBackspace = () => {
    setError(null);
    setDraft((prev) => prev.slice(0, -1));
  };

  const onCall = async () => {
    if (actionBusy) return;
    setError(null);
    const e164 = toE164(draft);
    if (!e164) {
      setError('Enter a valid phone number.');
      return;
    }
    setActionBusy(true);
    try {
      await dial(e164);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Call failed');
    } finally {
      setActionBusy(false);
    }
  };

  const onHangup = async () => {
    if (actionBusy) return;
    setError(null);
    setActionBusy(true);
    try {
      await hangup();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Hangup failed');
    } finally {
      setActionBusy(false);
    }
  };

  const handleStatus = async (name: string) => {
    setError(null);
    try {
      await setStatus(name);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to change status');
    }
  };

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Box>
        <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase' }}>
          Status
        </Typography>
        <Box sx={{ mt: 0.5 }}>
          <AgentStatusMenu
            current={agent?.status ?? null}
            options={agent?.availableStates ?? []}
            onSelect={handleStatus}
            disabled={!isReady}
          />
        </Box>
      </Box>

      {isCallActive ? (
        <Box
          sx={{
            p: 1.5,
            borderRadius: 1,
            bgcolor: 'v4.tableSelected',
            color: 'text.primary',
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {contact?.direction === 'inbound' ? 'Incoming from' : 'Calling'}
              </Typography>
              <Typography variant="h6" sx={{ fontSize: 16 }}>
                {formatForDisplay(contact?.phoneNumber ?? null)}
              </Typography>
            </Box>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Chip
                size="small"
                icon={<RecordIcon sx={{ fontSize: 12 }} />}
                label={recordingState === 'paused' ? 'Paused' : 'Recording'}
                color={recordingState === 'paused' ? 'warning' : 'error'}
                variant="outlined"
              />
              <Tooltip
                title={
                  recordingState === 'paused'
                    ? 'Resume recording'
                    : 'Pause recording (e.g. while collecting card details)'
                }
              >
                <span>
                  <IconButton
                    onClick={togglePauseResume}
                    disabled={recordingBusy}
                    size="small"
                    aria-label={
                      recordingState === 'paused' ? 'Resume recording' : 'Pause recording'
                    }
                  >
                    {recordingState === 'paused' ? (
                      <PlayIcon fontSize="small" />
                    ) : (
                      <PauseIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        </Box>
      ) : (
        <TextField
          fullWidth
          size="small"
          label="Phone number"
          placeholder="04XX XXX XXX or +61…"
          value={draft}
          onChange={(e) => {
            setError(null);
            setDraft(e.target.value);
          }}
          inputProps={{ inputMode: 'tel', 'aria-label': 'Phone number to dial' }}
          InputProps={{
            endAdornment: draft && (
              <IconButton size="small" onClick={onBackspace} aria-label="Delete last digit">
                <BackspaceIcon sx={{ fontSize: 18 }} />
              </IconButton>
            ),
          }}
        />
      )}

      <Dialpad onDigit={onDigit} disabled={!isReady || isCallActive} />

      {error && (
        <Alert severity="error" sx={{ py: 0.5 }}>
          {error}
        </Alert>
      )}

      {isCallActive ? (
        <Button
          fullWidth
          variant="contained"
          color="error"
          startIcon={<CallEndIcon />}
          onClick={onHangup}
          disabled={actionBusy}
        >
          Hang up
        </Button>
      ) : (
        <Button
          fullWidth
          variant="contained"
          color="success"
          startIcon={<PhoneIcon />}
          onClick={onCall}
          disabled={!isReady || !draft.trim() || actionBusy}
        >
          Call
        </Button>
      )}
    </Stack>
  );
};
